import { Component, ComponentInterface, Event, EventEmitter, forceUpdate, h, Host, JSX, Listen, Prop, Watch } from '@stencil/core';
import { Keys } from '../../shared/keys';
import { getEventCoordinates, Vector2d } from '../../utils/get-event-coordinates';
import { Ratio } from '../aspect-ratio/ratio';
import { ImageConfig } from './types';
import { WorkbenchRuntimeError } from '../../shared/errors';
import { WorkbenchTheme, DEFAULT_WORKBENCH_THEME } from '../../shared/themes';
import { bindHint360 } from './hint360/hint360';

const DRAG_PIXELS_TO_ROTATE = 25;

/**
 * @slot - Contents placed as an overlay on top of the element
 */
@Component({
  tag: 'wb-360-viewer',
  styleUrl: '360-viewer.scss',
  shadow: true,
})
export class ViewerComponent implements ComponentInterface {
  /** image configuration for lowRes and highRes images
   * @example `[{ lowRes: { src: '1-low.jpg' }, highRes: { src: '1-high.jpg' } }]`
   */
  @Prop()
  public images: Array<ImageConfig> = [];

  /** index of the current image */
  @Prop({ reflect: true, mutable: true })
  public currentIndex: number = 0;

  /** image aspect ratio */
  @Prop({ reflect: true })
  public ratio?: Ratio = '16x9';

  /** factor to increase/decrease the rotation speed i.e. `0.5` for half as fast, `2` for twice as fast */
  @Prop()
  public rotationSpeedFactor: number = 1;

  /** set to true to preload all high resolution images */
  @Prop()
  public preloadHighResImages: boolean = false;

  /** set to true to show the visual usage hint */
  @Prop({ reflect: true })
  public showHint: boolean = false;

  /** set to true to show the hint in loading state */
  @Prop()
  public loading: boolean = false;

  /** TODO Unset legacy assets as default: https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/3477 */
  /** set to false to apply logic related to the new car renders */
  @Prop({ reflect: true })
  public legacyAssets: boolean = true;

  /** the colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** event emitted when the image/rotation changes. emits the current image's index of the `images` array */
  @Event()
  public wbchange!: EventEmitter<number>;

  /** only applicable if preloadHighResImages=true
   * event emitted when all highres images are fully loaded */
  @Event()
  public wbload!: EventEmitter<number>;

  private dragStartCoords: Vector2d;
  private dragStartImageIndex: number;
  private imagesContainerEl!: HTMLDivElement;

  @Listen('mousedown')
  @Listen('touchstart', { passive: true })
  public mousedown(e: MouseEvent | TouchEvent): void {
    this.dragStartCoords = getEventCoordinates(e);
    this.dragStartImageIndex = this.currentIndex;

    this.addDragEventListeners();

    window.addEventListener('mouseup', this.removeDragEventListeners, { once: true });
    window.addEventListener('touchend', this.removeDragEventListeners, { once: true });
  }

  @Listen('keydown')
  public handleKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case Keys.LEFT:
        this.updateRotation(this.currentIndex - 1);
        return;
      case Keys.RIGHT:
        this.updateRotation(this.currentIndex + 1);
        return;
    }
  }

  @Listen('click')
  public focusRootElement(): void {
    this.imagesContainerEl.focus();
  }

  public connectedCallback(): void {
    this.updateImages();
  }

  @Watch('images')
  public updateImages(): void {
    if (!this.images.length) {
      return;
    }

    this.enhanceImageConfig();
    this.preloadImages();
    this.updateRotation(this.currentIndex); // rotation must also be updated here (fix for #2108)
  }

  @Watch('currentIndex')
  public updateRotation(newImageIndex: number): void {
    // note: images might not be available yet (if asynchronously applied)
    if (!this.images.length) {
      return;
    }

    if (newImageIndex < 0) {
      newImageIndex += this.images.length;
    }

    this.currentIndex = newImageIndex % this.images.length;
    this.wbchange.emit(this.currentIndex);

    const currentImageConfig = this.images[this.currentIndex];

    if (currentImageConfig.highRes.isLoaded === false) {
      this.loadHighResImage(currentImageConfig);
    }
  }

  private bindRef = (el: HTMLDivElement): void => {
    this.imagesContainerEl = el;
  };

  private getCurrentImageSrc(): string {
    const currentImage = this.images[this.currentIndex];

    if (currentImage.highRes.isLoaded) {
      return currentImage.highRes.src;
    }

    return currentImage.lowRes ? currentImage.lowRes.src : currentImage.highRes.src;
  }

  private enhanceImageConfig(): void {
    // This parameter in the car url, centers the car in the middle of the image for the new car renders
    // If not present, the new car renders it shows shifted to the bottom of the image
    // It only works with the new car renders, and we should force the car url to have that parameter, to make sure the car is centered in the image
    const centeringParameter = 'uni=m';
    this.images.forEach((i: ImageConfig) => {
      if (i.lowRes) {
        i.lowRes.img = new Image();
        if (!this.legacyAssets && !i.lowRes.src.includes(centeringParameter)) {
          i.lowRes.src = `${i.lowRes.src}&${centeringParameter}`;
        }
      }

      i.highRes.img = new Image();
      if (!this.legacyAssets && !i.highRes.src.includes(centeringParameter)) {
        i.highRes.src = `${i.highRes.src}&${centeringParameter}`;
      }
      i.highRes.isLoaded = false;
    });
  }

  private async preloadImages(): Promise<void> {
    const currentImageConfig = this.images[this.currentIndex];

    if (this.preloadHighResImages) {
      // load CURRENT lowRes image - if provided
      if (currentImageConfig.lowRes) {
        this.loadLowResImage(currentImageConfig);
      }

      /** load ALL highRes images beside the one which is already
       *  loaded by the current index
       */
      const promises = this.images.map(this.loadHighResImage);
      promises.splice(this.currentIndex, 1);
      await Promise.all(promises)
        .then(() => this.wbload.emit())
        .catch(this.throwImageLoadError);
    } else {
      // load ALL lowRes images - if provided
      this.images.filter((i) => i.lowRes).forEach(this.loadLowResImage);

      // load CURRENT highRes image
      this.loadHighResImage(currentImageConfig).catch(this.throwImageLoadError);
    }
  }

  private throwImageLoadError = (src): void => {
    throw new WorkbenchRuntimeError(`failed to load ${src}`, 'wb-360-viewer');
  };

  private loadLowResImage = (img: ImageConfig): void => {
    img.lowRes.img.src = img.lowRes.src;
  };

  private loadHighResImage = async (img: ImageConfig): Promise<void> => {
    return new Promise((resolve, reject) => {
      img.highRes.img.onload = () => {
        img.highRes.isLoaded = true;

        // force update if image is for current angle
        if (this.images[this.currentIndex].highRes.src === img.highRes.src) {
          forceUpdate(this);
        }
        resolve();
      };
      img.highRes.img.onerror = () => {
        reject(img.highRes.src);
      };
      img.highRes.img.src = img.highRes.src;
    });
  };

  private onDrag = (e: MouseEvent | TouchEvent): void => {
    const coords: Vector2d = getEventCoordinates(e);
    const dist = coords.x - this.dragStartCoords.x;
    const steps = Math.round((dist * this.rotationSpeedFactor) / DRAG_PIXELS_TO_ROTATE);
    this.updateRotation(this.dragStartImageIndex + steps);
  };

  private addDragEventListeners = (): void => {
    window.addEventListener('touchmove', this.onDrag, { passive: true });
    window.addEventListener('mousemove', this.onDrag);
  };

  private removeDragEventListeners = (): void => {
    window.removeEventListener('touchmove', this.onDrag);
    window.removeEventListener('mousemove', this.onDrag);
  };

  public render(): JSX.Element {
    const currentImageSource = this.getCurrentImageSrc();
    const backgroundImage = this.images.length ? `url(${currentImageSource})` : 'initial';
    const Hint360 = bindHint360(this);

    return (
      <Host class="wb-360-viewer" tabindex="0">
        <div aria-hidden="true" class="image" style={{ backgroundImage }} ref={this.bindRef} />
        {this.showHint && <Hint360 loading={this.loading} legacyAssets={this.legacyAssets} />}
        <div class="overlay">
          <slot />
        </div>
      </Host>
    );
  }
}
